import { ExperimentService } from './../../services/experiment.service';
import { Component, HostListener, OnInit } from '@angular/core';
import { ExperimentInstructionComponent } from '../../components/experiment-instruction/experiment-instruction.component';
import { ExperimentTrialComponent } from '../../components/experiment-trial/experiment-trial.component';
import { IntensityRatingDemoComponent } from '../../components/intensity-rating-demo/intensity-rating-demo.component';
import { ExperimentStage } from '../../models/experimentStageEnum';
import { Trial } from '../../models/trial';
import { EmotionOption } from '../../models/emotionOptionEnum';
import { DataService } from '../../services/data.service';
import { TrialService } from '../../services/trial.service';
import { TrialStage } from '../../models/trialStageEnum';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { PasswordGuardComponent } from '../../components/password-guard/password-guard.component';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-experiment',
  imports: [
    CommonModule,
    ExperimentInstructionComponent,
    ExperimentTrialComponent,
    IntensityRatingDemoComponent,
    PasswordGuardComponent,
  ],
  templateUrl: './experiment.component.html',
  styleUrl: './experiment.component.scss',
})
export class ExperimentComponent implements OnInit {
  isPasswordVerified = false;
  showPreparationScreen = false;
  isExperimentInitialized = false;
  participantIdentifier: string | null = null;
  trialSequenceNumber: number | null = null;
  isContinueFromOngoing = false;

  constructor(
    private experimentService: ExperimentService,
    private dataService: DataService,
    private trialService: TrialService,
    private router: Router,
    private route: ActivatedRoute,
    private dbService: NgxIndexedDBService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.participantIdentifier = params.get('participantIdentifier');
      const trialSequenceParam = params.get('trialSequenceNumber');
      this.trialSequenceNumber = trialSequenceParam ? parseInt(trialSequenceParam) : null;
      // Check if this is a continuation from ongoing experiments
      this.isContinueFromOngoing = window.history.state?.fromOngoing === true;
    });
  }

  onPasswordVerified() {
    this.isPasswordVerified = true;
    if (this.isContinueFromOngoing) {
      this.showPreparationScreen = true;
    } else {
      this.initializeExperiment();
    }
  }

  async initializeExperiment() {
    if (!this.participantIdentifier) {
      this.exitFullScreen();
      this.router.navigate(['/home']);
      return;
    }

    const participant: any = await firstValueFrom(
      this.dbService.getByKey('participants', this.participantIdentifier)
    );

    if (participant) {
      // If we don't have trialSequenceNumber from URL (e.g., continuing experiment),
      // get it from the stored participant data
      if (!this.trialSequenceNumber && participant.trialSequenceNumber) {
        this.trialSequenceNumber = participant.trialSequenceNumber;
      }
      
      // Validate that the URL trial sequence number matches the stored one
      if (this.trialSequenceNumber && participant.trialSequenceNumber && 
          this.trialSequenceNumber !== participant.trialSequenceNumber) {
        alert(`試驗序列號碼不匹配。URL中的序列號碼: ${this.trialSequenceNumber}, 儲存的序列號碼: ${participant.trialSequenceNumber}`);
        this.exitFullScreen();
        this.router.navigate(['/home']);
        return;
      }
      
      if (!this.trialSequenceNumber) {
        alert('無法找到測試序列號碼，請重新開始測試');
        this.exitFullScreen();
        this.router.navigate(['/home']);
        return;
      }
      
      try {
        await this.experimentService.loadProgress(participant.experimentProgress, this.trialSequenceNumber);
      } catch (error) {
        if (error instanceof Error && error.message.includes('Trial sequence number mismatch')) {
          alert(`數據不一致錯誤: ${error.message}\n請聯繫管理員或重新開始測試。`);
        } else {
          alert(`載入實驗進度時發生錯誤: ${error}`);
        }
        this.exitFullScreen();
        this.router.navigate(['/home']);
        return;
      }
    } else {
      if (!this.trialSequenceNumber) {
        this.exitFullScreen();
        this.router.navigate(['/home']);
        return;
      }
      await this.experimentService.initExperiment(this.participantIdentifier, this.trialSequenceNumber);
    }
    this.isExperimentInitialized = true;
  }

  onPreparationComplete() {
    this.showPreparationScreen = false;
    this.initializeExperiment();
  }

  showInstruction(): boolean {
    return (
      this.isExperimentInitialized &&
      this.experimentService.isOnExperimentInstruction()
    );
  }

  showIntensityRatingDemo(): boolean {
    return (
      this.isExperimentInitialized &&
      this.experimentService.isOnIntensityRatingDemo()
    );
  }

  instructionMessage(): string {
    return this.experimentService.experimentInstructionMessage();
  }

  showTrial(): boolean {
    return (
      this.isExperimentInitialized &&
      this.experimentService.isOnExperimentTrial()
    );
  }

  currentTrial(): Trial | undefined {
    return this.experimentService.currentTrial;
  }

  currentTrialStage(): TrialStage {
    return this.trialService.currentStage;
  }

  exitFullScreen() {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  }

  async updateExperimentState() {
    if (this.experimentService.currentStage === ExperimentStage.Complete) {
      this.exitFullScreen();
      this.router.navigate(['/home']);
      return;
    }

    this.experimentService.goToNextState();

    if (this.experimentService.currentStage === ExperimentStage.Experiment) {
      await this.experimentService.goToNextTrial();
    }
  }

  async continueExperiment() {
    if (this.experimentService.currentStage === ExperimentStage.Pause) {
      this.experimentService.continueExperiment();
      await this.experimentService.goToNextTrial();
    }
  }

  updateLastPracticeClassificationCorrect(selectedOption: EmotionOption) {
    const isCorrect = this.experimentService.practiceTrial?.correctAnswer === selectedOption;
    if (isCorrect) {
      this.trialService.goToIntensityRating();
    }
  }

  saveClassificationResponse(selectedOption: EmotionOption) {
    this.dataService.saveEmotionClassificationResp(selectedOption);
    if (selectedOption === EmotionOption.NEUTRAL) {
      // For NEUTRAL responses in experiment, skip intensity rating and save with -1
      this.dataService.saveNeutralIntensityResp();
      this.taskComplete();
    } else {
      this.trialService.goToIntensityRating();
      this.dataService.startCountingIntensityRating();
    }
  }

  saveIntensityResponse(confirmedIntensity: number) {
    this.dataService.saveIntensityResp(confirmedIntensity);
  }

  async taskComplete() {
    if (this.experimentService.currentStage === ExperimentStage.Training) {
      // Hide the trial image but keep the trial data
      this.experimentService.hideTrialImage();
      await new Promise(resolve => setTimeout(resolve, 200));
      this.experimentService.goToNextState();
    } else {
      await this.dataService.saveResp(
        this.experimentService.participantIdentifier,
        this.experimentService.trialSequenceNumber
      );

      if (this.experimentService.isLastTrial()) {
        this.experimentService.goToNextState();
      } else {
        if (this.experimentService.pauseExperimentBeforeNextTrial) {
          // Hide the trial image before pausing to prevent showing previous image on resume
          this.experimentService.hideTrialImage();
          await new Promise(resolve => setTimeout(resolve, 200));
          // Implementation notes
          // After unpause, goToNextTrial() will get called in continueExperiment()
          this.experimentService.pauseExperiment();
        } else {
          // Hide the trial image but keep the trial data
          this.experimentService.hideTrialImage();
          await new Promise(resolve => setTimeout(resolve, 200));
          await this.experimentService.goToNextTrial();
        }
      }
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key.toLowerCase() === 'p') {
      this.experimentService.pauseExperimentRequested();
    } else if (event.key === 'q') {
      this.exitFullScreen();
      this.router.navigate(['/home']);
    }
  }

  trialImgSrc() {
    return this.experimentService.getCurrentTrialImgSrc();
  }
}
